import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Plus, RefreshCw, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PaymentStatusBadge } from '@/components/shared/StatusBadges'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PaginatedTabTable } from '@/components/shared/PaginatedTabTable'
import {
  ClientTabSection,
  clientTabFilterOptionClass,
  clientTabActionBtnClass,
  clientTabActionsCellClass,
  clientTabActionsColClass,
  clientTabFirstTdClass,
  clientTabFilterTriggerClass,
  clientTabSearchInputClass,
  clientTabPrimaryBtnClass,
  clientTabViewDetailsBtnClass,
} from '@/components/clients/tabs/ClientTabSection'
import { useClientPayments } from '@/hooks/useClients'
import { invalidatePortalMetricQueries } from '@/hooks/usePortalMetrics'
import { useListParams } from '@/hooks/useListParams'
import { clientsApi } from '@/services/clients'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { useQueryClient } from '@tanstack/react-query'
import { formatCurrency, formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import {
  PAYMENT_FAILURE_REASON_LABELS,
  PAYMENT_METHOD_LABELS,
  type Payment,
  type PaymentFailureReason,
  type PaymentMethod,
  type PaymentStatus,
  type UpdatePaymentStatusRequest,
} from '@/types'

const ALL = 'all'
const orangeActionBtnClass = cn(
  clientTabActionBtnClass,
  'border border-transparent p-[7px] hover:border-[#ff5a1f] hover:text-[#ff5a1f]'
)

type PStatus = UpdatePaymentStatusRequest['status']
const FAILURE_REASONS: PaymentFailureReason[] = [
  'not_received_by_due_date',
  'partial_payment',
  'invoice_disputed',
  'system_behavior',
  'stripe_payment_failed',
]

// 2.4.13 Client details — payments tab.
export function PaymentsTab({
  clientId,
  canCreatePayment = false,
  onOpenJob,
  onOpenPayment,
}: {
  clientId: string
  canCreatePayment?: boolean
  onOpenJob?: (jobId: string) => void
  onOpenPayment?: (paymentId: string) => void
}) {
  const { t } = useTranslation(['clients', 'common'])
  const navigate = useNavigate()
  const qc = useQueryClient()
  const {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    filters,
    setFilters,
    query,
  } = useListParams<{ status?: string }>({})
  const { data, isLoading, isError } = useClientPayments(clientId, query)

  const [target, setTarget] = useState<Payment | null>(null)
  const [newStatus, setNewStatus] = useState<PStatus>('paid')
  const [failureReason, setFailureReason] = useState<PaymentFailureReason | ''>(
    ''
  )
  const openJob = (jobId: string) => {
    if (onOpenJob) onOpenJob(jobId)
    else navigate(`/calendar?job=${jobId}`)
  }
  const openPayment = (paymentId: string) => {
    if (onOpenPayment) onOpenPayment(paymentId)
    else navigate(`/payments/${paymentId}`)
  }

  const confirmUpdate = async () => {
    if (!target) return
    if (newStatus === 'failed' && !failureReason) {
      toast({
        variant: 'destructive',
        title: t('payments.dialog.failureReasonRequired'),
      })
      return
    }
    try {
      await clientsApi.updatePaymentStatus(target.id, {
        status: newStatus,
        ...(newStatus === 'failed' && failureReason
          ? { failure_reason: failureReason }
          : {}),
      })
      toast({ variant: 'success', title: t('payments.statusUpdated') })
      qc.invalidateQueries({ queryKey: ['clients', clientId, 'payments'] })
      invalidatePortalMetricQueries(qc)
      setTarget(null)
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('payments.updateError')),
      })
    }
  }

  return (
    <ClientTabSection
      title={t('detail.tabs.payments')}
      toolbar={
        <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
          <div className='flex min-w-0 flex-wrap items-center gap-4'>
            <Select
              value={filters.status ?? ALL}
              onValueChange={v =>
                setFilters({ status: v === ALL ? undefined : v })
              }
            >
              <SelectTrigger className={clientTabFilterTriggerClass}>
                <SelectValue placeholder={t('tabFilters.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} className={clientTabFilterOptionClass}>
                  {t('tabFilters.allStatuses')}
                </SelectItem>
                <SelectItem
                  value='pending'
                  className={clientTabFilterOptionClass}
                >
                  {t('payments.filters.pending')}
                </SelectItem>
                <SelectItem value='paid' className={clientTabFilterOptionClass}>
                  {t('payments.filters.completed')}
                </SelectItem>
                <SelectItem
                  value='failed'
                  className={clientTabFilterOptionClass}
                >
                  {t('payments.filters.failed')}
                </SelectItem>
                <SelectItem
                  value='refunded'
                  className={clientTabFilterOptionClass}
                >
                  {t('payments.filters.refunded')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className='flex flex-col gap-4 sm:flex-row sm:items-center'>
            <div className='relative min-w-0 w-full lg:w-[320px]'>
              <Search className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#3d434e]' />
              <Input
                value={search}
                onChange={e => onSearchChange(e.target.value)}
                placeholder={t('payments.searchPlaceholder')}
                maxLength={50}
                className={cn(clientTabSearchInputClass, 'max-w-[320px]')}
              />
            </div>
            {canCreatePayment && (
              <Button
                className={clientTabPrimaryBtnClass}
                onClick={() => navigate(`/payments/new?client_id=${clientId}`)}
              >
                <Plus className='size-4' /> {t('payments.addNew')}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <PaginatedTabTable<Payment>
        clientHistoryStyle
        data={data}
        isLoading={isLoading}
        isError={isError}
        emptyMessage={t('payments.empty')}
        errorMessage={t('payments.error')}
        page={page}
        pageSize={limit}
        onPageChange={setPage}
        onPageSizeChange={setLimit}
        rowKey={p => p.id}
        onRowClick={p => openPayment(p.id)}
        columns={[
          {
            header: t('payments.columns.paymentId'),
            cellClassName: clientTabFirstTdClass,
            cell: p => (
              <div className='text-sm leading-[1.4]'>
                {p.reference ?? p.id.slice(0, 8)}
              </div>
            ),
          },
          {
            header: t('payments.columns.linkedJob'),
            cell: p => {
              const jobId = p.job_id

              return jobId ? (
                <button
                  type='button'
                  className='text-sm text-[#171a20] hover:text-[#ff5a1f] hover:underline'
                  onClick={e => {
                    e.stopPropagation()
                    openJob(jobId)
                  }}
                >
                  {p.job_reference ?? '—'}
                </button>
              ) : (
                <span className='text-sm text-[#171a20]'>-</span>
              )
            },
          },
          {
            header: t('payments.columns.amount'),
            className: 'text-center',
            cell: p => (
              <span className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                {formatCurrency(p.amount, 'EUR', 2)}
              </span>
            ),
          },
          {
            header: t('payments.columns.method'),
            className: 'text-center',
            cell: p => (
              <span className='text-sm leading-[1.4] text-[#171a20]'>
                {PAYMENT_METHOD_LABELS[p.method as PaymentMethod] ?? p.method}
              </span>
            ),
          },
          {
            header: t('payments.columns.status'),
            className: 'text-center',
            cell: p => (
              <div className='space-y-1'>
                <PaymentStatusBadge status={p.status as PaymentStatus} />
                {p.status === 'failed' && p.failure_reason && (
                  <span className='block text-xs text-muted-foreground'>
                    {PAYMENT_FAILURE_REASON_LABELS[p.failure_reason]}
                  </span>
                )}
              </div>
            ),
          },
          {
            header: t('payments.columns.collectionDate'),
            cell: p => (
              <span className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                {formatDate(p.paid_date)}
              </span>
            ),
          },
          {
            header: t('list.columns.actions'),
            className: clientTabActionsColClass,
            cell: p => (
              <div
                className={clientTabActionsCellClass}
                onClick={e => e.stopPropagation()}
              >
                <TruncatedTextTooltip
                  alwaysShow
                  closeOnPointerDown
                  className='inline-flex shrink-0'
                  content={t('payments.actions.updateStatus')}
                  delayDuration={0}
                >
                  <button
                    type='button'
                    className={orangeActionBtnClass}
                    aria-label={t('payments.actions.updateStatus')}
                    onClick={() => {
                      setTarget(p)
                      setNewStatus(p.status as PStatus)
                      setFailureReason(
                        (p.failure_reason ?? '') as PaymentFailureReason | ''
                      )
                    }}
                  >
                    <RefreshCw className='h-4 w-4' />
                  </button>
                </TruncatedTextTooltip>
                <button
                  type='button'
                  onClick={() => openPayment(p.id)}
                  className={clientTabViewDetailsBtnClass}
                >
                  {t('common:actions.view')}
                  <ArrowRight className='h-4 w-4 shrink-0' />
                </button>
              </div>
            ),
          },
        ]}
      />

      <Dialog open={!!target} onOpenChange={o => !o && setTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('payments.dialog.title')}</DialogTitle>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <label className='text-sm font-medium'>
                {t('payments.dialog.newStatus')}
              </label>
              <Select
                value={newStatus}
                onValueChange={v => {
                  const s = v as PStatus
                  setNewStatus(s)
                  if (s !== 'failed') setFailureReason('')
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='pending'>
                    {t('payments.filters.pending')}
                  </SelectItem>
                  <SelectItem value='paid'>
                    {t('payments.filters.completed')}
                  </SelectItem>
                  <SelectItem value='failed'>
                    {t('payments.filters.failed')}
                  </SelectItem>
                  {(target?.status === 'paid' ||
                    target?.status === 'refunded') && (
                    <SelectItem value='refunded'>
                      {t('payments.filters.refunded')}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            {newStatus === 'failed' && (
              <div className='space-y-2'>
                <label className='text-sm font-medium'>
                  {t('payments.dialog.failureReason')}
                </label>
                <Select
                  value={failureReason || undefined}
                  onValueChange={v =>
                    setFailureReason(v as PaymentFailureReason)
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t('payments.dialog.selectFailureReason')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {FAILURE_REASONS.map(r => (
                      <SelectItem key={r} value={r}>
                        {PAYMENT_FAILURE_REASON_LABELS[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setTarget(null)}>
              {t('common:actions.cancel')}
            </Button>
            <Button onClick={confirmUpdate}>
              {t('common:actions.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ClientTabSection>
  )
}
